/*
 * Swift Ink — scroll reveal
 * ------------------------------------------------------------
 * Fades and lifts page sections into view as they are scrolled to.
 *
 * Product cards in the shop grid are deliberately NOT revealed: holding each
 * card back until it was on screen (and its photo had loaded) made the grid
 * look like it loaded slowly while scrolling. Cards now show straight away and
 * only their photos fade in (assets/si-smooth.js).
 *
 * Safeguards:
 *  - Nothing is hidden by CSS alone. The `si-sr-ready` class is added by this
 *    script, so if JS fails or is blocked the whole page renders normally.
 *  - Only elements BELOW the fold at init are hidden. Anything already painted
 *    is left alone, which avoids a visible flash and keeps the hero out of the
 *    animation entirely so LCP is unaffected.
 *  - `transform` is stripped once the animation finishes. A lingering transform
 *    creates a containing block for position:fixed descendants, which would
 *    break the slide cart and any sticky bar that ends up inside a revealed
 *    section.
 *  - Header, footer, cart elements and anything fixed or sticky are skipped
 *    outright.
 *  - Honours prefers-reduced-motion.
 */
(function () {
  'use strict';

  if (!('IntersectionObserver' in window)) return;

  var mq = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
  if (mq && mq.matches) return;

  var SELECTOR = [
    'main .shopify-section',
    'main > section'
  ].join(',');

  // Anything that is, or lives inside, chrome that must not move.
  var SKIP_WITHIN = [
    'header',
    'footer',
    '[id^="amp-"]',
    '[class*="slidecart"]',
    '[class*="slide-cart"]',
    '[class*="ampcart"]',
    '.si-bundle-bar',
    'dialog',
    '[role="dialog"]'
  ].join(',');

  var MIN_HEIGHT = 40;
  var observer;

  function skippable(el) {
    if (el.dataset.siSr) return true;
    if (el.closest && el.closest(SKIP_WITHIN)) return true;

    var cs = window.getComputedStyle(el);
    if (cs.position === 'fixed' || cs.position === 'sticky') return true;
    if (cs.display === 'none' || cs.visibility === 'hidden') return true;

    return false;
  }

  function prepare(el) {
    if (skippable(el)) return;

    var rect = el.getBoundingClientRect();
    if (rect.height < MIN_HEIGHT) return;

    // Already on screen — leave it visible. No flash, and the hero is exempt.
    if (rect.top < (window.innerHeight || 0)) {
      el.dataset.siSr = 'skip';
      return;
    }

    el.dataset.siSr = 'pending';
    el.classList.add('si-sr');
    observer.observe(el);
  }

  function settle(el) {
    el.classList.add('si-sr--done');
    el.style.transitionDelay = '';
    el.dataset.siSr = 'done';
  }

  function reveal(el) {
    el.classList.add('si-sr--in');
    el.dataset.siSr = 'in';

    var done = false;
    function finish() {
      if (done) return;
      done = true;
      el.removeEventListener('transitionend', onEnd);
      settle(el);
    }
    function onEnd(e) {
      if (e.target === el && e.propertyName === 'transform') finish();
    }

    el.addEventListener('transitionend', onEnd);
    // Fallback in case the transition never fires (tab backgrounded, etc).
    setTimeout(finish, 1400);
  }

  observer = new IntersectionObserver(function (entries) {
    for (var i = 0; i < entries.length; i++) {
      if (!entries[i].isIntersecting) continue;
      observer.unobserve(entries[i].target);
      reveal(entries[i].target);
    }
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0 });

  function boot() {
    document.documentElement.classList.add('si-sr-ready');
    document.querySelectorAll(SELECTOR).forEach(prepare);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
